import { Component, computed, effect, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { provideLuxonDateAdapter } from '@angular/material-luxon-adapter';
import { MatButtonModule } from '@angular/material/button';
import { MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatTableModule } from '@angular/material/table';
import { ApexAnnotations, ApexAxisChartSeries, ApexChart, ApexDataLabels, ApexFill, ApexGrid, ApexLegend, ApexMarkers, ApexNonAxisChartSeries, ApexPlotOptions, ApexResponsive, ApexStates, ApexStroke, ApexTheme, ApexTitleSubtitle, ApexTooltip, ApexXAxis, ApexYAxis, NgApexchartsModule } from "ng-apexcharts";
import { DashboardStatsService } from '../../../services/dashboard-stats.service';
import { MY_DATE_FORMATS } from '../../../utils/utils';
import { DateTime } from 'luxon';

const serieTitles: Record<string, string> = {
    'contact.count': 'Contacts',
    'contact.supplier.count': 'Suppliers',
    'contact.non_supplier.count': 'Non-suppliers',
    'organisation.count': 'Organisations',
    'organisation.supplier.count': 'Suppliers',
    'organisation.non_supplier.count': 'Non-suppliers',
    'organisation.member.count': 'Members',
    'organisation.non_member.count': 'Non-members',
    'project.count': 'Projects',
    'event.count': 'Events',
};

export interface MetricPoint {
    bucketStart: number;
    value: number;
}

export interface MetricEntry {
    metricKey: string;
    metricTitle: string;
    period: string;
    points: MetricPoint[];
}


function getMetricsForKeys(
    dict: Record<string, MetricEntry>,
    keys: string[]) {
    return keys.map(k => dict[k]).filter(Boolean);
}

function stats2Series(metrics: MetricEntry[]): { name: string, data: number[] | number[][] }[] {
    const series = [];

    for (const m of metrics) {
        series.push({ name: serieTitles[m.metricKey] ?? 'N/A', data: m.points.map(v => [v.bucketStart, v.value]) })
    }
    return series;
}

export type ChartOptions = {
    series?: ApexAxisChartSeries | ApexNonAxisChartSeries;
    chart?: ApexChart;
    xaxis?: ApexXAxis;
    yaxis?: ApexYAxis | ApexYAxis[];
    title?: ApexTitleSubtitle;
    subtitle?: ApexTitleSubtitle;
    dataLabels?: ApexDataLabels;
    stroke?: ApexStroke;
    fill?: ApexFill;
    legend?: ApexLegend;
    tooltip?: ApexTooltip;
    markers?: ApexMarkers;
    plotOptions?: ApexPlotOptions;
    responsive?: ApexResponsive[];
    grid?: ApexGrid;
    annotations?: ApexAnnotations;
    states?: ApexStates;
    theme?: ApexTheme;
    colors?: string[];
    labels?: any;
};




@Component({
    selector: 'app-dashboard',
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatTableModule,
        MatButtonModule,
        NgApexchartsModule,
    ],
    providers: [
        provideLuxonDateAdapter(),
        { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS },
        { provide: MAT_DATE_LOCALE, useValue: 'fr' },
    ],
    templateUrl: './dashboard.component.html',
    styleUrl: './dashboard.component.scss'
})
export class DashboardComponent {
    readonly columnsToDisplay = ['label', 'value'];
    private readonly statsService = inject(DashboardStatsService);
    stats = this.statsService.stats;

    BASE_CHART: Partial<ChartOptions> = {
        chart: {
            type: 'line',
            height: 350,
            width: 550
        },
        markers: {
            size: 3,
            shape: 'square'
        },
        series: [],
        xaxis: {
            labels: {
                formatter: function (value) {
                    return DateTime.fromSeconds(value as number).toLocaleString();
                }
            }
        },
        yaxis: {
            title: {
                text: 'Count',
            },
            min: 0,
        },
        fill: {
            opacity: 1
        },
        tooltip: {
            y: {
                formatter: (val) => {
                    return val.toString();
                }
            }
        },
        dataLabels: {
            enabled: false
        },
        stroke: {
            curve: 'straight',
            width: 2
        },
        plotOptions: {
            bar: {
                horizontal: false,
                columnWidth: '55%',
                borderRadius: 5,
                borderRadiusApplication: 'end'
            },
        },
        title: {
            text: 'Product Trends by Month',
            align: 'left',
        },
        legend: {
            show: true,
            position: 'left',
        }
    };

    charts: ChartOptions[] = [
        {
            ...this.BASE_CHART,
            title: { text: 'Contacts' },
            series: [],
        },
        {
            ...this.BASE_CHART,
            title: { text: 'Organisations' },
            series: [],
        },
        {
            ...this.BASE_CHART,
            title: { text: 'Projects' },
            series: [],
        },
        {
            ...this.BASE_CHART,
            title: { text: 'Events' },
            series: [],
        }
    ];

    constructor() {
        effect(() => {
            const value = this.stats.value();
            const status = this.stats.status();

            if (this.stats.isLoading()) {
                console.log('Loading stats…');
            }

            if (this.stats.error()) {
                console.error('Failed to load stats:', this.stats.error());
            }

            if (this.stats.hasValue()) {
                // console.log('Stats loaded:', value);
                this.processMetrics(value);
            }
        });
    }

    onRefresh() {
        this.statsService.refresh();
    }

    processMetrics(allMetrics: any) {
        let selectedMetrics = getMetricsForKeys(allMetrics, ['contact.count', 'contact.supplier.count', 'contact.non_supplier.count']);
        this.charts[0].series = stats2Series(selectedMetrics);

        selectedMetrics = getMetricsForKeys(allMetrics, ['organisation.count', 'organisation.supplier.count',
            'organisation.non_supplier.count', 'organisation.member.count', 'organisation.non_member.count']);
        this.charts[1].series = stats2Series(selectedMetrics);


        selectedMetrics = getMetricsForKeys(allMetrics, ['project.count']);
        this.charts[2].series = stats2Series(selectedMetrics);


        selectedMetrics = getMetricsForKeys(allMetrics, ['event.count']);
        this.charts[3].series = stats2Series(selectedMetrics);
    }
}
